import { FC, FormEvent, useEffect, useMemo, useState } from "react";
import { IDropdownOption, Stack } from "@fluentui/react";
import { DropDown, Label } from "cayo.ui";
import { ItemsPerPage } from "@cayo/shared/api/types/schema";
import { newQuery, setCondition } from "@cayo/shared/components/GridContainer/gridModel/Query";
import {
  ConditionType,
  IMaxResultsCondition,
  IQuery,
  DispatchAction,
} from "@cayo/shared/components/GridContainer/types";

const DEFAULT_ITEMS_PER_PAGE: ItemsPerPage = {
  name: "20",
  size: 20,
};

interface IMaxResultsDropDownProps {
  maxResults: ItemsPerPage[];
  query?: IQuery;
  dispatch: DispatchAction;
  previewQueryId?: string | null;
}

const MaxResultsDropDown: FC<IMaxResultsDropDownProps> = ({
  maxResults,
  query,
  dispatch,
  previewQueryId,
}) => {
  const [itemsToShow, setItemsToShow] = useState<string>();

  useEffect(() => {
    const savedItemsSize = (
      query?.conditions.find((c) => c.kind === ConditionType.MaxResults) as IMaxResultsCondition
    )?.value;
    const itemsPerPage =
      maxResults.find((r) => r.size.toString() === savedItemsSize)?.name ??
      DEFAULT_ITEMS_PER_PAGE.name;

    setItemsToShow(itemsPerPage);
  }, [query?.conditions, maxResults]);

  const options: IDropdownOption[] = useMemo(
    () =>
      maxResults
        ? maxResults.map(({ name, size }) => ({
            key: name,
            text: name,
            value: size,
          }))
        : [],
    [maxResults]
  );

  const handleSelectValue = (
    value: string,
    event?: FormEvent<HTMLInputElement | HTMLTextAreaElement> | null
  ) => {
    // event is null means that callback wasn't triggered by user
    // done to prevent additional execute request from triggering
    if (event === null) {
      return;
    }

    const itemsPerPage = maxResults.find((r) => r.name === value) ?? DEFAULT_ITEMS_PER_PAGE;
    setItemsToShow(itemsPerPage.name);

    const actualQuery = query ?? newQuery();
    const conditionQuery = setCondition(actualQuery, {
      kind: ConditionType.MaxResults,
      value: itemsPerPage?.size.toString(),
    });

    dispatch({
      kind: "changeQuery",
      previewQuery: Boolean(previewQueryId),
      query: conditionQuery,
    });
  };

  return (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4, padding: "0 4px" }}>
      <Label label="Show:" htmlFor="max-results-dropdown" />
      <DropDown
        id="max-results-dropdown"
        options={options}
        value={itemsToShow}
        setValue={handleSelectValue}
      />
    </Stack>
  );
};

export default MaxResultsDropDown;
